import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import RecordFormsModal from './RecordFormsModal'
import { assignmentHasResponse } from './formResponseHelpers'

const RETURNED = new Set(['submitted', 'accepted', 'in_review'])

async function fetchAssignments(params) {
  const qs = new URLSearchParams(params)
  const res = await authFetch(getLocalApiUrl(`/api/forms/assignments?${qs}`))
  const j = await res.json()
  if (!res.ok || !j.success) return []
  return j.data || []
}

function mergeAssignments(lists) {
  const seen = new Set()
  const out = []
  for (const list of lists) {
    for (const row of list || []) {
      if (!row?.id || seen.has(row.id)) continue
      seen.add(row.id)
      out.push(row)
    }
  }
  return out
}

/**
 * Compact launcher for record forms — opens a modal for selection, preview, send, and tracking.
 */
export default function RecordFormsPanel({
  subjectType,
  subjectId,
  subjectLabel = '',
  patronId = '',
  emailTo = '',
  smsTo = '',
  placement = '',
  readOnly = false,
  compact = false,
  title = 'Forms',
  description = '',
  onAssignmentsChange,
  onAssigned,
}) {
  const [open, setOpen] = useState(false)
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!subjectId && !patronId) return
    setLoading(true)
    try {
      const queries = []
      if (subjectType && subjectId) queries.push(fetchAssignments({ subjectType, subjectId }))
      if (patronId && String(patronId) !== String(subjectId)) {
        queries.push(fetchAssignments({ subjectType: 'patron', subjectId: patronId }))
      }
      const lists = await Promise.all(queries)
      setAssignments(mergeAssignments(lists))
    } catch {
      setAssignments([])
    } finally {
      setLoading(false)
    }
  }, [subjectType, subjectId, patronId])

  useEffect(() => { load() }, [load])

  const onAssignmentsChangeRef = useRef(onAssignmentsChange)
  onAssignmentsChangeRef.current = onAssignmentsChange

  const handleAssignmentsChange = useCallback(() => {
    load()
    onAssignmentsChangeRef.current?.()
  }, [load])

  const returnedCount = assignments.filter((a) => RETURNED.has(a.status)).length
  const cancelledCount = assignments.filter((a) => a.status === 'cancelled').length
  const openCount = assignments.filter((a) => !RETURNED.has(a.status) && a.status !== 'cancelled').length

  const summary = loading
    ? 'Loading…'
    : assignments.length
      ? `${returnedCount} returned · ${openCount} still out${cancelledCount ? ` · ${cancelledCount} cancelled` : ''}`
      : 'Send or track forms on this record'

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full rounded-xl border border-slate-200 bg-white text-left transition-colors hover:border-teal-300 hover:bg-teal-50/30 ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <DocumentTextIcon className={`shrink-0 text-teal-700 ${compact ? 'h-4 w-4 mt-0.5' : 'h-5 w-5 mt-0.5'}`} />
            <div className="min-w-0">
              <div className={`font-semibold text-teal-900 ${compact ? 'text-[13px]' : 'text-sm'}`}>{title}</div>
              <div className="text-[11px] text-slate-500 truncate">{summary}</div>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center justify-center rounded-lg bg-[#073B4D] text-white text-xs font-semibold px-2.5 py-1.5">
            Open
          </span>
        </div>
      </button>

      <RecordFormsModal
        open={open}
        onClose={() => setOpen(false)}
        subjectType={subjectType}
        subjectId={subjectId}
        subjectLabel={subjectLabel}
        patronId={patronId}
        emailTo={emailTo}
        smsTo={smsTo}
        placement={placement}
        readOnly={readOnly}
        title={title}
        description={description}
        onAssignmentsChange={handleAssignmentsChange}
        onAssigned={onAssigned}
      />
    </>
  )
}
